import { defineStore } from 'pinia';
import * as SearchRepo from '../repository/search';
import * as EavRepo from '@/repository/eav';
import { useTokenStore } from './token';
import { debounce } from 'debounce';
import { CountryUtil, Message, MessageClass, Offer, Request } from '@fibl/oxs-common';
import type { EAV } from '@fibl/zdb-common';
import * as MessageRepo from '@/repository/message';
import { useRelatedStore } from './related';
import { useWebsiteStore } from './website';
import { getI18n } from '@/util/i18n';

export type MessageSearchFilter = {
	archived: boolean;
	unread: boolean;
	recipient: string;
	context: Message.MessageContextKeys | 'announcement' | 'no-context' | undefined;
};

const defaultTableFilter: MessageSearchFilter = {
	archived: false,
	unread: false,
	recipient: '',
	context: undefined,
};

const debouncedLoadMessages = debounce(async function load(
	store: ReturnType<typeof useMessagingStore>,
) {
	store.conversations = [];
	const filter = store.searchFilter;
	const res: EAV.Entity<Message.MessageData>[] = await SearchRepo.search(
		'message',
		filter,
		store.pagination,
		`indexfields.lastMessageDate:${store.sorting}`,
	);
	if (res.length < store.pageSize) {
		store.totalResults = store.pagination.start + res.length;
	} else {
		store.totalResults = await SearchRepo.countEntries('message', filter);
	}
	store.notificationCount = 0;
	store.conversations = await Promise.all(
		res.map(async (row) => {
			let obj = new MessageClass();
			obj.fromStorage(row);
			let party = store.getParty(obj);
			obj.setCurrentParty(party);
			return obj;
		}),
	);
},
350);

export const useMessagingStore = defineStore('messaging', {
	state: () => ({
		messageEc: '',
		companyLinks: [] as string[],
		conversations: [] as MessageClass[],
		conversationForRequest: null as MessageClass | null,
		currentRequestContext: '',
		notificationCount: 0,
		unreadMessageCount: 0,
		// table and pagination
		pageSize: 10,
		totalResults: 0,
		currentPage: 1,
		sorting: 'desc',
		tableFilter: JSON.parse(JSON.stringify(defaultTableFilter)) as MessageSearchFilter,
		loadedOnce: false,
		signatureId: '',
		messageExistsCheck: {} as Record<string, boolean>,
	}),
	persist: {
		paths: ['sorting', 'tableFilter', 'signatureId'],
	},
	getters: {
		searchFilter(): SearchRepo.SearchFilter {
			const tokenstore = useTokenStore();
			const filter: SearchRepo.SearchFilter = { $and: [] };
			const accountId = tokenstore.main.account;
			const partyFilter: SearchRepo.SearchFilter = { $and: [] };
			filter.$and.push({
				field: `/data/parties/account`,
				comparison: 'eq',
				value: accountId,
			});
			if (this.tableFilter.unread) {
				filter.$and.push({
					field: `/indexfields/unreadParties`,
					comparison: 'eq',
					value: accountId,
				});
			}
			if (this.tableFilter.context) {
				if (this.tableFilter.context === 'announcement') {
					filter.$and.push({
						field: '/data/convoType',
						comparison: 'eq',
						value: 'announcement',
					});
				} else if (this.tableFilter.context === 'no-context') {
					filter.$and.push({
						$not: {
							field: '/data',
							comparison: 'exists',
							value: 'context',
						},
					});
				} else {
					filter.$and.push({
						field: '/data/context',
						comparison: 'exists',
						value: this.tableFilter.context,
					});
				}
			}
			if (this.tableFilter.archived) {
				partyFilter.$and.push({
					field: '/data/parties/archived',
					comparison: 'eq',
					value: true,
				});
			} else {
				partyFilter.$and.push({
					$or: [
						{
							field: '/data/parties/archived',
							comparison: 'eq',
							value: false,
						},
						{
							$not: {
								field: '/data/parties',
								comparison: 'exists',
								value: 'archived',
							},
						},
					],
				});
			}
			// if (this.tableFilter.folder) {
			// 	partyFilter.$and.push({
			// 		field: '/data/parties/folder',
			// 		comparison: 'eq',
			// 		value: this.tableFilter.folder,
			// 	});
			// }
			return {
				$and: [
					{
						$nested: {
							path: '/data/parties',
							match: partyFilter,
						},
					},
					filter,
				],
			};
		},
		pagination(): { start: number; limit: number } {
			return {
				start: (this.currentPage - 1) * this.pageSize,
				limit: this.pageSize,
			};
		},
	},
	actions: {
		resetFilters() {
			this.tableFilter = {
				archived: false,
				unread: false,
				recipient: '',
				context: undefined,
			};
		},
		async loadConversations() {
			await debouncedLoadMessages(this);
		},
		async getUnreadMessageCount() {
			const tokenStore = useTokenStore();
			const accountId = tokenStore.main.account;
			if (!accountId) return;
			const filter: SearchRepo.SearchFilter = {
				$and: [
					{
						field: '/indexfields/unreadParties',
						comparison: 'eq',
						value: accountId,
					},
					{
						$nested: {
							path: '/data/parties',
							match: {
								$and: [
									{
										$or: [
											{
												field: '/data/parties/archived',
												comparison: 'eq',
												value: false,
											},
											{
												$not: {
													field: '/data/parties',
													comparison: 'exists',
													value: 'archived',
												},
											},
										],
									},
								],
							},
						},
					},
				],
			};
			this.unreadMessageCount = await SearchRepo.countEntries('message', filter);
		},
		// async saveConversation(conversation: MessageClass) {
		// 	try {
		// 		return await EavRepo.updateEntity(conversation, 'anr');
		// 	} catch (error) {
		// 		const message = await getErrorMessage(error);
		// 		if (message.includes('eav: there is no change after applying the patch'))
		// 			console.log(message, error);
		// 		else throw new Error('Failed to save conversation: ' + error);
		// 	}
		// },
		async initNewConvo() {
			const tokenstore = useTokenStore();
			const now = new Date().toJSON();
			// const isFibl = tokenstore.currentAccountInstitution.length > 0;
			const conversation = new MessageClass();
			let ec = await EavRepo.getEntityclassByExternalId('message');
			let as = await EavRepo.getAttributesetByExternalId('message');
			conversation.fromStorage({
				entityclass: ec.id,
				attributeset: as.id,
				id: '',
				rev: 0,
				data: {},
				created: '',
				updated: '',
				attributerev: as.rev
			});
			const payload: Message.MessageData = {
				parties: [
					{
						account: tokenstore.accountId,
						initiator: true,
						canWrite: true,
					},
				],
				messages: [],
				context: {},
				convoType: 'conversation',
			};
			conversation.fromData(payload);
			conversation.setCurrentParty(0);
			return conversation;
		},
		async updateConversation(
			conversation: MessageClass,
			newMessage: string,
			newAttachments: Message.MessageAttachment[],
			asDraft: boolean,
			contextId: Message.MessageContextKeys | undefined,
			recipient: string,
			offerId: string,
			supId: string,
		) {
			const now = new Date();
			if (recipient) {
				conversation.parties.push({
					account: recipient,
					initiator: false,
					canWrite: true,
				});
			}
			if (asDraft) {
				conversation.getCurrentParty().draft = {
					message: newMessage,
					attachments: newAttachments,
				};
			} else {
				conversation.getCurrentParty().draft = undefined;
				const messages = conversation.messages as Message.Message[];
				messages.push({
					party: conversation.getCurrentPartyIndex(),
					sent: now.toJSON(),
					content: newMessage,
					attachments: newAttachments.length ? newAttachments : undefined,
				});
			}
			if (conversation.getId()) {
				await MessageRepo.addToConversation(
					conversation.getId(),
					conversation.messages[conversation.messages.length - 1],
				);
			} else {
				if (contextId) {
					const tokenStore = useTokenStore();
					await MessageRepo.sendConversation(
						conversation.messages[conversation.messages.length - 1],
						contextId,
						offerId,
						supId,
						undefined,
						tokenStore.main.details?.staticData?.country as CountryUtil.CountryCode
					);
					if (offerId) this.setMessageExists(contextId, offerId);
					if (supId) this.setMessageExists(contextId, supId);
				} else {
					const config = conversation.toData() as Message.MessageData;
					if (config.context && !Object.keys(config.context).length)
						delete config.context;
					const newMessageEnt = await MessageRepo.createNewConversation(config);
					await this.loadConversations();
					conversation.fromEntity(newMessageEnt);
				}
			}
			conversation.getCurrentParty().lastReadMessages = new Date().toJSON();
			let inList = this.conversations.find((e) => e.getId() === conversation.getId());
			if (inList) {
				inList.fromEntity(conversation.toEntity());
			} else {
				this.conversations.unshift(conversation);
			}
		},
		async setLastRead(row: MessageClass) {
			let lastmessagesent = row.messages[row.messages.length - 1]?.sent;
			if (!lastmessagesent) return;
			let now = new Date();
			let party = row.getCurrentParty();
			if (!party.lastReadMessages || party.lastReadMessages < lastmessagesent) {
				row.parties[row.getCurrentPartyIndex()].lastReadMessages = now.toJSON();
				const config: Message.ChangeParties = {
					conversation: row.getId(),
					party: {
						lastReadMessages: now.toJSON(),
					},
				};
				await MessageRepo.changePartyProps(config);
				if (this.unreadMessageCount > 0) this.unreadMessageCount -= 1;
			}
		},
		getParty(conversation: MessageClass) {
			const i18n = getI18n();
			const tokenStore = useTokenStore();
			try {
				return MessageClass.getParty(conversation, tokenStore.accountId);
			} catch (error) {
				throw new Error(i18n.t('stores.currentPartyError') + `: ` + error);
			}
		},
		getContext(convo: MessageClass) {
			const relatedStore = useRelatedStore();
			const websiteStore = useWebsiteStore();
			const lang = websiteStore.locale;
			const params: Record<string, string> = {};
			const context = convo.context ?? {};
			if (!Object.keys(context).length) {
				const initiator = convo.parties.find((p) => p.initiator);
				if (initiator) {
					const initiatorName = relatedStore.getAccountName(initiator.account);
					params.sender = initiatorName;
				} else {
					params.sender = getI18n().t('views.message.sysGenerated');
				}
			} else {
				const contextKey = Object.keys(convo.context)[0];
				if (contextKey === 'missingSpecies') {
					params.accName = relatedStore.getAccountName(context.missingSpecies!);
				}
				if (contextKey === 'supAcceptanceInCountry') {
					params.accName = relatedStore.getAccountName(convo.getSupIdFromContext());
				}
				if (contextKey === 'request') {
					const request = relatedStore.byId(
						context.request!,
					) as EAV.Entity<Request.RequestProcess>;

					if (!request) relatedStore.addEntity(context.request!);
					params.hid = request ? String(request.data.hid) : '-';
				}
				if (contextKey === 'agrToSupOffer' || contextKey === 'autToSupOfferInCountry') {
					const offerId = convo.getOfferIdFromContext();
					if (!offerId) return;
					let offer = relatedStore.byId(offerId) as EAV.Entity<Offer.Offer>;
					if (!offer) relatedStore.addEntity(offerId);
					if (!offer) return;
					params.species = relatedStore.getSpeciesLabel(offer.data.species);
					if (offer.data.sort) {
						params.sort = relatedStore.getLabel(offer.data.sort);
					}
				}
			}
			return convo.getSubjectFromContext(lang, params);
		},
		async markMessageUnread(id: string, party: Message.Party) {
			const config = {
				conversation: id,
			};
			await MessageRepo.markMessageUnread(config);
		},
		async archiveConversation(id: string, party: Message.Party, archived: boolean) {
			const config: Message.ChangeParties = {
				conversation: id,
				party: {
					archived: archived,
				},
			};
			await MessageRepo.changePartyProps(config);
		},
		async loadConversationIfExist(context: Message.Context, isAnnouncement: boolean) {
			const filter: SearchRepo.SearchFilter = { $or: [] };
			const contextkey = Object.keys(context)[0] as Message.MessageContextKeys
			filter.$or.push({
				field: `/data/context/${contextkey}`,
				comparison: 'eq',
				value: context[contextkey]!,
			});
			if (isAnnouncement) {
				const tokenStore = useTokenStore();
				filter.$or.push({
					$and: [
						{ field: '/data/convoType', comparison: 'eq', value: 'announcement' },
						{
							field: '/data/parties/account',
							comparison: 'eq',
							value: tokenStore.accountId,
						},
						{
							field: '/data/parties/initiator',
							comparison: 'eq',
							value: true,
						},
					],
				});
			}
			const res: EAV.Entity<Message.MessageData>[] = await SearchRepo.search(
				'message',
				filter,
			);
			return res;
		},

		async batchMessageExistsCheck(
			contextKey: Message.MessageContextKeys,
			contextIds: string[],
		): Promise<Record<string, boolean>> {
			const res: Record<string, boolean> = {};
			const filter: SearchRepo.SearchFilter = { $or: [] };
			for (let id of contextIds) {
				const key = `${contextKey}_${id}`;
				if (key in this.messageExistsCheck) {
					res[id] = this.messageExistsCheck[key];
				} else {
					filter.$or.push({
						field: `/data/context/${contextKey}`,
						comparison: 'eq',
						value: id,
					});
					res[id] = false;
					this.messageExistsCheck[key] = false;
				}
			}
			if (filter.$or.length) {
				let msgs = await SearchRepo.findAll<Message.MessageData>('message', filter);
				for (let conversation of msgs) {
					const id = conversation.data.context![contextKey] as string;
					const key = `${contextKey}_${id}`;
					this.messageExistsCheck[key] = true;
					res[id] = true;
				}
			}
			return res;
		},
		setMessageExists(contextKey: Message.MessageContextKeys, contextId: string) {
			const key = `${contextKey}_${contextId}`;
			this.messageExistsCheck[key] = true;
		},
	},
});
